import { SUIT_NAMES, SUIT_SYMBOLS, SUITS } from '../cards.js';
import { blocksPending, isPenaltyCanceller, isPowerCard, isWild, penaltyContribution, } from './effects.js';
import { IllegalMoveError, } from './types.js';
export { isPowerCard };
export function topCard(state) {
    const top = state.discardPile[state.discardPile.length - 1];
    if (!top)
        throw new Error('discard pile is empty');
    return top;
}
export function findPlayer(state, playerId) {
    const player = state.players.find((p) => p.id === playerId);
    if (!player)
        throw new IllegalMoveError(`unknown player ${playerId}`);
    return player;
}
export function currentPlayer(state) {
    const player = state.players[state.currentPlayerIndex];
    if (!player)
        throw new Error('current player index out of range');
    return player;
}
/** Plain suit-or-rank matching, ignoring wilds and special cases. */
function matchesTop(card, state) {
    const top = topCard(state);
    return card.rank === top.rank || card.suit === state.activeSuit;
}
/**
 * Can this card be led (played as the first card of a turn) in this position?
 * Later cards in a multi-card play are checked separately — they only need to
 * match the rank of the leading card.
 */
export function canLeadWith(card, state) {
    const { rules } = state;
    // A live pick-up stack narrows the options to cards that answer it. Crucially
    // they must ALSO match normally, which is what makes the traditional chain
    // work: an Ace of Hearts can only be answered by a heart or another Ace.
    if (state.pending) {
        if (!matchesTop(card, state))
            return false;
        // A blocker answers only its own kind of pick-up, and does so even when
        // stacking is switched off.
        if (blocksPending(card, rules, state.pending.kinds))
            return true;
        // A blocker that cannot answer this stack is no help at all.
        if (isPenaltyCanceller(card, rules))
            return false;
        return rules.penaltiesStack && penaltyContribution(card, rules) !== null;
    }
    if (state.mirrorSuit) {
        return card.suit === state.mirrorSuit || card.rank === '7' || isWild(card, rules);
    }
    if (isWild(card, rules))
        return true;
    return matchesTop(card, state);
}
/** Playing these cards would empty the hand — is that allowed here? */
function checkFinishingCard(cards, player, rules) {
    if (!rules.cannotFinishOnPowerCard)
        return;
    if (cards.length !== player.hand.length)
        return;
    const last = cards[cards.length - 1];
    if (isPowerCard(last, rules)) {
        throw new IllegalMoveError(`You cannot go out on a ${last.rank} under these rules.`);
    }
}
/**
 * Validates an arbitrary set of card ids as a single play and returns the cards
 * in play order. Throws IllegalMoveError with a message meant for the player.
 */
export function validatePlay(state, playerId, cardIds) {
    if (state.phase !== 'playing') {
        throw new IllegalMoveError('The game is not accepting plays right now.');
    }
    const player = findPlayer(state, playerId);
    if (currentPlayer(state).id !== playerId) {
        throw new IllegalMoveError('It is not your turn.');
    }
    if (cardIds.length === 0) {
        throw new IllegalMoveError('No cards selected.');
    }
    if (new Set(cardIds).size !== cardIds.length) {
        throw new IllegalMoveError('The same card was played twice.');
    }
    const cards = [];
    for (const id of cardIds) {
        const card = player.hand.find((c) => c.id === id);
        if (!card)
            throw new IllegalMoveError('You do not hold that card.');
        cards.push(card);
    }
    const lead = cards[0];
    if (!canLeadWith(lead, state)) {
        throw new IllegalMoveError(describeWhyNotPlayable(lead, state));
    }
    if (cards.length > 1) {
        if (!state.rules.playMultipleSameRank) {
            throw new IllegalMoveError('Only one card may be played per turn.');
        }
        if (!cards.every((c) => c.rank === lead.rank)) {
            throw new IllegalMoveError('Multiple cards must all be the same rank.');
        }
        // A blocking card kills the whole stack on its own, so combining it with
        // cards that would add to that stack is contradictory.
        if (state.pending && cards.some((c) => isPenaltyCanceller(c, state.rules))) {
            throw new IllegalMoveError('A blocking card cancels the pick-up on its own.');
        }
    }
    checkFinishingCard(cards, player, state.rules);
    return cards;
}
export function describeUnplayable(ctx, card) {
    if (ctx.pending) {
        if (!ctx.rules.penaltiesStack) {
            return `You must take ${ctx.pending.amount} — pick-ups cannot be passed on under these rules.`;
        }
        if (isPenaltyCanceller(card, ctx.rules)) {
            return `That blocks a different kind of pick-up. Play a matching card or take ${ctx.pending.amount}.`;
        }
        return `There is a pick-up of ${ctx.pending.amount} to answer — play a matching pick-up card, block it, or take them.`;
    }
    if (ctx.mirrorSuit) {
        return `A seven was played — you must follow ${SUIT_NAMES[ctx.mirrorSuit]} or play another seven.`;
    }
    return `${card.rank}${SUIT_SYMBOLS[card.suit]} matches neither ${ctx.topCard.rank} nor ${SUIT_NAMES[ctx.activeSuit]}.`;
}
function describeWhyNotPlayable(card, state) {
    return describeUnplayable({
        topCard: topCard(state),
        activeSuit: state.activeSuit,
        pending: state.pending,
        mirrorSuit: state.mirrorSuit,
        rules: state.rules,
    }, card);
}
export function playableCards(state, player) {
    return player.hand.filter((card) => {
        if (!canLeadWith(card, state))
            return false;
        if (state.rules.cannotFinishOnPowerCard && player.hand.length === 1 && isPowerCard(card, state.rules)) {
            return false;
        }
        return true;
    });
}
export function hasPlayableCard(state, player) {
    return playableCards(state, player).length > 0;
}
/**
 * The moves available to a player right now.
 *
 * For multi-card plays this offers the single card and the maximal same-rank
 * group rather than every subset — enough for bots and for UI hints, while
 * `validatePlay` still accepts any legal combination the player builds by hand.
 */
export function legalMoves(state, playerId) {
    if (state.phase === 'finished')
        return [];
    if (state.phase === 'choosingSuit') {
        if (state.awaitingSuitFrom !== playerId)
            return [];
        return SUITS.map((suit) => ({ kind: 'chooseSuit', suit }));
    }
    const player = state.players.find((p) => p.id === playerId);
    if (!player || currentPlayer(state).id !== playerId || player.finishedPosition !== null)
        return [];
    const moves = [];
    const playable = playableCards(state, player);
    const seenRanks = new Set();
    for (const card of playable) {
        const requiresSuitChoice = isSuitChoiceCard(card, state);
        moves.push({ kind: 'play', cardIds: [card.id], cards: [card], requiresSuitChoice });
        if (state.rules.playMultipleSameRank && !seenRanks.has(card.rank)) {
            seenRanks.add(card.rank);
            const group = groupForMultiPlay(state, player, card);
            if (group.length > 1) {
                moves.push({
                    kind: 'play',
                    cardIds: group.map((c) => c.id),
                    cards: group,
                    requiresSuitChoice,
                });
            }
        }
    }
    if (state.pending) {
        // Taking the pick-up is always available as the alternative to countering.
        moves.push({ kind: 'draw', count: state.pending.amount, reason: 'penalty' });
    }
    else if (state.hasDrawnThisTurn) {
        moves.push({ kind: 'pass' });
    }
    else if (playable.length === 0 || state.rules.bluffing) {
        // Standard Switch: you must play if you can, so drawing is only offered
        // when nothing in hand is legal. Bluffing lifts that restriction, letting a
        // player take a card rather than reveal that they had one to lay.
        moves.push({ kind: 'draw', count: 1, reason: 'turn' });
    }
    return moves;
}
function isSuitChoiceCard(card, state) {
    return ((card.rank === 'A' && state.rules.aces === 'changeSuit') ||
        (card.rank === 'J' && state.rules.jacks === 'changeSuit'));
}
/** All same-rank cards that may legally accompany `lead` in one play. */
function groupForMultiPlay(state, player, lead) {
    let group = player.hand.filter((c) => c.rank === lead.rank);
    if (state.pending) {
        // Never bundle a blocking card with cards that would raise the stack.
        group = isPenaltyCanceller(lead, state.rules)
            ? [lead]
            : group.filter((c) => !isPenaltyCanceller(c, state.rules));
    }
    const rest = group.filter((c) => c.id !== lead.id);
    const ordered = [lead, ...rest];
    if (state.rules.cannotFinishOnPowerCard && ordered.length === player.hand.length) {
        const last = ordered[ordered.length - 1];
        if (isPowerCard(last, state.rules))
            return [lead];
    }
    return ordered;
}
//# sourceMappingURL=legal.js.map